import { AxiosError } from 'axios';
import { axiosInstance } from './config';
import type { 
  ApiResponse, 
  PaginatedApiResponse,
  ApiError, 
  RequestConfig,
  ErrorResponse,
  ApiRequestResponse
} from './types';

/**
 * The error every ApiClient method throws.
 *
 * It extends Error rather than being a bare object literal because callers overwhelmingly
 * narrow with `error instanceof Error ? error.message : 'Something failed'` — a plain
 * object fails that check, so the server's own message was discarded and the generic
 * fallback shown instead. That is why e.g. a Flow sync rejected with "flow generator:
 * batched task ...: type "regular" not supported" surfaced in the UI as nothing more
 * than "Failed to sync flow".
 *
 * The ApiError fields (code, status, errors) are kept as own properties, so the
 * existing `(error as ApiError).code` readers keep working unchanged.
 */
export class ApiClientError extends Error implements ApiError {
  code?: number | string;
  status?: number;
  errors?: Record<string, string[]>;

  constructor(message: string, options?: Omit<ApiError, 'message'>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = options?.code;
    this.status = options?.status;
    this.errors = options?.errors;
  }
}

export class ApiClient {
  private static handleError(error: AxiosError<ErrorResponse>): never {
    let message = 'An unexpected error occurred';
    let code: number | string | undefined;

    if (error.response) {
      // Server responded with error
      const errorData = error.response.data;

      if (errorData.data) {
        message = errorData.data.message;
        code = errorData.data.code;
      } else {
        message = error.message;
      }
    } else if (error.request) {
      // Request made but no response
      message = 'No response received from server';
    }

    throw new ApiClientError(message, { code, status: error.response?.status });
  }

  private static validateResponse<T>(response: any): ApiResponse<T> | PaginatedApiResponse<T> {
    if (!response.data || !response.data.status) {
      throw new Error('Invalid API response format');
    }

    if (response.data.status === 'error') {
      throw new ApiClientError(response.data.data.message, { code: response.data.data.code });
    }

    return response.data;
  }

  static async get<T = void>(url: string, config?: RequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.get<T, ApiRequestResponse<T>>(url, config);

      // If response type is blob, return the raw response
      if (config?.responseType === 'blob') {
        return response.data;
      }

      const validatedResponse = this.validateResponse<T>(response);
      return validatedResponse.data as T;
    } catch (error) {
      return this.handleError(error as AxiosError<ErrorResponse>);
    }
  }

  static async post<T = void>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.post<T, ApiRequestResponse<T>>(url, data, config);

      // If response type is blob, return the raw response
      if (config?.responseType === 'blob') {
        return response.data;
      }

      // Handle void responses (like logout, delete operations)
      if (response.status === 204 || !response.data) {
        return undefined as T;
      }

      const validatedResponse = this.validateResponse<T>(response);
      return validatedResponse.data as T;
    } catch (error) {
      return this.handleError(error as AxiosError<ErrorResponse>);
    }
  }

  static async put<T = void>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.put<T, ApiRequestResponse<T>>(url, data, config);

      // Handle void responses
      if (response.status === 204 || !response.data) {
        return undefined as T;
      }

      const validatedResponse = this.validateResponse<T>(response);
      return validatedResponse.data as T;
    } catch (error) {
      return this.handleError(error as AxiosError<ErrorResponse>);
    }
  }

  static async patch<T = void>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.patch<T, ApiRequestResponse<T>>(url, data, config);

      // Handle void responses
      if (response.status === 204 || !response.data) {
        return undefined as T;
      }

      const validatedResponse = this.validateResponse<T>(response);
      return validatedResponse.data as T;
    } catch (error) {
      return this.handleError(error as AxiosError<ErrorResponse>);
    }
  }

  static async delete<T = void>(url: string, config?: RequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.delete<T, ApiRequestResponse<T>>(url,config);

      // Handle void responses (most delete operations return 204 No Content)
      if (response.status === 204 || !response.data) {
        return undefined as T;
      }

      const validatedResponse = this.validateResponse<T>(response);
      return validatedResponse.data as T;
    } catch (error) {
      return this.handleError(error as AxiosError<ErrorResponse>);
    }
  }
}